import React, { useEffect, useRef, useState, forwardRef } from 'react'

import InfoModal from '../InfoModal/InfoModal'
import Icon from '../Icon2/Icon'
import './VariantList.css'
import { VariantListModal } from './VariantListModal'
import { Truncate } from '../Truncate/Truncate'
import TagAsSelect from '../TagAsSelect/TagAsSelect'

export type VariantListProps = {
  /**
   *Specifies the name of selected variant.
   */
  selectedVariantName?: string
  /**
   *Lets you fetch the variant list using the API
   */
  fetchVariantList?: Function
  /**
   *Lets you display a variant list display in disbaled state
   */
  isListDisabled?: boolean
  /**
   *Custom message to display when the list is disabled
   */
  listDisabledMessage?: string | false
  /**
   *Pass an ID that can be used for testing purposes. It is applied as a data attribute(data-test-id).
   */
  testId?: string
  /**
   *Pass onclickHandler to handle click on variant
   */
  onClickHandler?: Function
  /**
   *Pass onBaseClickHandler to handle click on variant
   */
  onBaseClickHandler?: Function
  /**
   *Specifies the name of selected locale of the entry.
   */
  selectedLocaleName?: string
  /**
   *Specifies the name of selected ct of the entry.
   */
  selectedContentTypeName?: string
  /**
   *Specifies the number of variant group that will be fetched in API.
   */
  batchSize?: number
  /**
   *Specifies the number of variant group that will be fetched in API.
   */
  variantModalCustomClass?: string
  prefix?: React.ReactNode
  multiSelectTag?: boolean
  withPortal?: boolean
  portalId?: string
  portalProps?: any
  disableNonCreatedVariant?: boolean
  /**
   * Determines whether button to open variant list is disabled or not
   */
  disabled?: boolean
  /**
   * Optional footer slot content to render in the variant list modal
   */
  footerSlot?: React.ReactNode
  /**
   * Specifies the group name of the variant
   */
  selectedVariantGroupName?: string
}

const VariantList = forwardRef<HTMLDivElement, VariantListProps>((props: VariantListProps, customRef: any) => {

  const {
    isListDisabled = false,
    listDisabledMessage,
    fetchVariantList,
    onClickHandler,
    onBaseClickHandler,
    testId = "cs-entry-variant-switcher",
    batchSize = 10,
    selectedVariantName,
    selectedLocaleName,
    selectedContentTypeName,
    variantModalCustomClass,
    prefix,
    multiSelectTag = false,
    withPortal,
    portalId,
    portalProps,
    disableNonCreatedVariant = false,
    disabled,
    footerSlot,
    selectedVariantGroupName
  } = props
  const variantModalTarget = customRef ? customRef : useRef()

  const [variantName, setVariantName] = useState<string>(selectedVariantName || '[Base Entry]')
  const [isVariantListModalOpen, setIsVariantListModalOpen] = useState<boolean>(false)
  const [variantPills, setVariantPills] = useState([])

  const showVariantModal = () => {
    const modalProps = {
      fetchVariantList,
      isListDisabled,
      listDisabledMessage,
      onClickHandler,
      onBaseClickHandler,
      setVariantName,
      batchSize,
      selectedVariantName,
      selectedLocaleName,
      selectedContentTypeName,
      disableNonCreatedVariant,
      footerSlot
    }
    let hasPortalProps: any = {}
    if (withPortal && portalId && document.getElementById(portalId)) {
      hasPortalProps = {
        portal: {
          sourceElem: variantModalTarget.current,
          destinationElem: portalProps?.destinationElem || document.getElementById(portalId),
          AdjustLeftOffset: portalProps?.AdjustLeftOffset || 0,
          // AdjustTopOffset: portalProps?.AdjustTopOffset || -475,
        }
      }
    }

    if (isVariantListModalOpen) return
    if (disabled) return

    setIsVariantListModalOpen(true)
    InfoModal({
      component: (props: any) => VariantListModal({ ...props, ...modalProps, multiSelectTag: multiSelectTag, setVariantPills: setVariantPills, variantPills: variantPills }),
      modalProps: {
        targetNodeOrId: variantModalTarget.current,
        closeOnOverlayClick: true,
        onClose: (): void => {
          setIsVariantListModalOpen(false)
        },
        className: `VariantList__modal-wrapper ${variantModalCustomClass}`,
        ...hasPortalProps
      },
      alignment: 'top-left'
    })
  }

  const handleTagUpdate = (tags: any) => {
    setVariantPills(tags)
  }

  return (
    <>
      {
        multiSelectTag ?
          <TagAsSelect readonly={false} showCount={false} tags={variantPills} updateTag={handleTagUpdate} seeMoreCount={100} selectText={''} placeholder={'Select variants'} error={false} width={"500"} showSingleItem={false} openModalCb={showVariantModal} showEmptyState={true} showTooltip={true} maxCount={10} version={'v2'} />
          :
          <div className={`VariantList ${disabled ? 'VariantList--disabled' : ''}`} data-test-id={testId} onClick={showVariantModal}>
            <div className={`VariantList__header ${!!selectedVariantGroupName ? 'VariantList__header--with-group-name' : ''}`}>
              {prefix && <div data-test-id="cs-dropdown-prefix" className='Dropdown__prefix'>
                {prefix}
              </div>}
              <div className={`VariantList__header__value ${!!selectedVariantGroupName ? 'VariantList__header__value--with-group-name' : ''}`}>
                <Truncate truncateFrom='end' isResponsive testId='cs-selected-variant-name'>{`${selectedVariantName ? selectedVariantName : variantName} `}</Truncate>
                {selectedVariantGroupName && 
                <div className="VariantList__header__value__group-name">
                  <Truncate  truncateFrom='end' isResponsive testId='cs-selected-variant-group-name'>{`${selectedVariantGroupName}`}</Truncate>
                </div>
                }
              </div>
              <Icon
                className={
                  `ml-5 Dropdown__chevron variant__suffix__wrapper ${isVariantListModalOpen === true ? 'toggle-icon' : ''}`
                }
                icon="CaretDownNew"
                version="v2"
                size="medium"
              />
            </div>
          </div>
      }
      <div ref={variantModalTarget}></div>
    </>
  )
})

export default VariantList
